import { useState, useRef, useEffect, useMemo } from 'react';
import Lenis from 'lenis';
import { useUi } from '@weareconceptstudio/core';

//* HOC's
import { withDataContext } from '@/context';

//* Components
import Items from './Items';
import ImageLoadBlocker from '../ImageLoadBlocker';
import AnimItems from './AnimItems';

//* Styles
import InfiniteScrollAppendStyle from './style';

//! If you make a change, also change it in "Infinite Item & Preloader".
const loadedCount = 12;

const InfiniteScrollAppend = ({ items, preloader, autoScrollTime = 20000, speed = 0.5, threshold = 3500 }) => {
	const { isBurgerMenuOpen, winWidth } = useUi();

	const [percent, setPercent] = useState(0);
	const [active, setActive] = useState(false);
	const [finallyAnim, setFinallyAnim] = useState(false);
	const [list, setList] = useState([...items]);

	const containerRef = useRef(null);
	const scrollContentRef = useRef(null);
	const lastScrollTimeRef = useRef(Date.now());
	const lenisRef = useRef(null);
	const lastScrollPositionRef = useRef(0);

	const isDesktopScreen = useMemo(() => winWidth >= 1280, [winWidth]);

	const shuffleArray = (array) => {
		const shuffled = [...array];

		for (let i = shuffled.length - 1; i > 0; i--) {
			const j = Math.floor(Math.random() * (i + 1));
			[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
		}

		return shuffled;
	};

	const checkAppend = (scrollY) => {
		const container = containerRef.current;
		if (!container) return;

		const scrollHeight = container.scrollHeight;
		const containerHeight = container.clientHeight;

		if (scrollHeight - (scrollY + containerHeight) < threshold) {
			const shuffledItems = shuffleArray(items).slice(0, 12);
			setList((prev) => [...prev, ...shuffledItems]);
		}
	};

	//! Start Auto Scroll
	const startAutoScroll = () => {
		const lenis = lenisRef.current;
		if (!lenis) return;

		let autoScrollActive = true;
		lenis._autoScrollRunning = true;
		lenis._autoScrollType = 'auto';

		const autoScroll = () => {
			if (!autoScrollActive) return;

			const newScrollPos = lenis.scroll + speed;
			lastScrollPositionRef.current = newScrollPos;

			lenis._autoScrollType = 'auto';
			lenis.scrollTo(newScrollPos, { immediate: true });

			checkAppend(newScrollPos);

			requestAnimationFrame(autoScroll);
		};

		lenisRef.current._cancelAutoScroll = () => {
			autoScrollActive = false;
			lenis._autoScrollRunning = false;
		};

		requestAnimationFrame(autoScroll);
	};

	//! Stop Auto Scroll
	const stopAutoScroll = () => {
		const lenis = lenisRef.current;
		if (!lenis || !lenis._cancelAutoScroll) return;

		lenis._cancelAutoScroll();
	};

	//! Scroll lock/unlock logic
	useEffect(() => {
		let timerID;
		const preventScroll = (e) => e.preventDefault();
		const html = document.documentElement;

		html.style.pointerEvents = 'none';

		window.addEventListener('wheel', preventScroll, { passive: false });
		window.addEventListener('touchmove', preventScroll, { passive: false });

		document.documentElement.classList.add('touch-action');
		document.body.style.overflow = 'hidden';
		document.body.style.touchAction = 'none';

		if (percent >= 90) {
			timerID = setTimeout(() => {
				window.removeEventListener('wheel', preventScroll);
				window.removeEventListener('touchmove', preventScroll);

				document.documentElement.classList.remove('touch-action');
				document.body.style.overflow = '';
				document.body.style.touchAction = '';
				html.style.pointerEvents = 'auto';
			}, 3500);
		}

		return () => {
			clearTimeout(timerID);

			window.removeEventListener('wheel', preventScroll);
			window.removeEventListener('touchmove', preventScroll);

			document.body.style.overflow = '';
			document.body.style.touchAction = '';
			document.documentElement.classList.remove('touch-action');

			html.style.pointerEvents = 'auto';
		};
	}, [percent]);

	//! First Load
	useEffect(() => {
		let timeId;

		if (percent >= 90) {
			setActive(true);

			timeId = setTimeout(() => {
				setFinallyAnim(true);
			}, 3000);

			lastScrollTimeRef.current = Date.now();
		}

		return () => {
			clearTimeout(timeId);
		};
	}, [percent]);

	//! Auto Scroll Logic
	useEffect(() => {
		let idleChecker;

		if (active && !isBurgerMenuOpen && percent === 100) {
			idleChecker = setInterval(() => {
				const now = Date.now();
				const idleTime = now - lastScrollTimeRef.current;

				if (idleTime > autoScrollTime && !lenisRef.current?._autoScrollRunning) {
					startAutoScroll();
				}
			}, autoScrollTime);
		}

		return () => {
			clearInterval(idleChecker);
			stopAutoScroll();
		};
	}, [active, isBurgerMenuOpen, percent, autoScrollTime]);

	//! Setup Lenis
	useEffect(() => {
		const wrapper = containerRef.current;
		const content = scrollContentRef.current;
		if (!wrapper || !content) return;

		const lenis = new Lenis({
			wrapper,
			content,
			smooth: true,
			easing: 3,
			gestureOrientation: 'vertical',
			lerp: 0.1,
			duration: isDesktopScreen ? 0 : 1,
			syncTouch: true,
			smoothTouch: true,
			touchMultiplier: isDesktopScreen ? 1 : 0, //! Touch scroll will feel faster
			wheelMultiplier: isDesktopScreen ? 1 : 0.7, //! Desktop wheel scroll will feel smoother
			anchorScroll: true,
			allowNestedScroll: true,
		});

		lenisRef.current = lenis;

		const raf = (time) => {
			lenis.raf(time);
			requestAnimationFrame(raf);
		};

		requestAnimationFrame(raf);

		lenis.on('scroll', ({ scroll }) => {
			const lenis = lenisRef.current;

			if (lenis?._autoScrollRunning && lenis?._autoScrollType === 'auto') {
				lenis._autoScrollType = null;
				return;
			}

			if (lenis?._autoScrollRunning) {
				stopAutoScroll();
			}

			lastScrollTimeRef.current = Date.now();
			checkAppend(scroll);
		});

		return () => {
			lenis.destroy();
		};
	}, [isDesktopScreen]);

	useEffect(() => {
		const el = containerRef.current;
		if (!el) return;

		el.addEventListener('mousedown', stopAutoScroll, { passive: true });
		el.addEventListener('mousemove', stopAutoScroll, { passive: true });
		el.addEventListener('mouseleave', stopAutoScroll, { passive: true });
		el.addEventListener('mouseup', stopAutoScroll, { passive: true });
		el.addEventListener('touchmove', stopAutoScroll, { passive: true });
		el.addEventListener('touchstart', stopAutoScroll, { passive: true });
		el.addEventListener('touchend', stopAutoScroll, { passive: true });

		return () => {
			el.removeEventListener('mousedown', stopAutoScroll);
			el.removeEventListener('mousemove', stopAutoScroll);
			el.removeEventListener('mouseleave', stopAutoScroll);
			el.removeEventListener('mouseup', stopAutoScroll);
			el.removeEventListener('touchmove', stopAutoScroll);
			el.removeEventListener('touchstart', stopAutoScroll);
			el.removeEventListener('touchend', stopAutoScroll);
		};
	}, []);

	return (
		<>
			{!preloader ? (
				<ImageLoadBlocker
					isLoadedCount={loadedCount}
					needOnlyIsReady
					returnPercent={setPercent}
				/>
			) : null}

			<InfiniteScrollAppendStyle ref={containerRef}>
				<div ref={scrollContentRef}>
					<AnimItems
						list={list.slice(0, loadedCount)}
						percent={percent}
						className={`${active ? 'anim-active' : ''} ${finallyAnim ? 'finally-anim' : ''}`}
					/>

					<Items
						containerRef={containerRef}
						list={list}
						percent={percent}
						className={active ? 'initial-active' : ''}
					/>
				</div>
			</InfiniteScrollAppendStyle>
		</>
	);
};

export default withDataContext(InfiniteScrollAppend, ['preloader']);
